// src/pages/Inventory/Replenishment.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/axios";
import { AlertTriangle, Loader, PlusCircle, Trash2 } from "lucide-react";

/* ----------------------------- formatting ----------------------------- */

function toNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function moneyGBP(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "£0.00";
  return `£${n.toFixed(2)}`;
}

// Prefer a readable quantity display.
// If base_uom is provided (g/ml/each), display it with smart scaling.
// If base_uom is missing, fall back to raw number.
function formatQtyWithUnit(qty, baseUom) {
  const n = toNum(qty, 0);

  const u = String(baseUom || "").toLowerCase();
  if (u === "g") {
    // show kg if large
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(3)} kg`;
    return `${n.toFixed(3)} g`;
  }
  if (u === "ml") {
    // show L if large
    if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(3)} L`;
    return `${n.toFixed(3)} ml`;
  }
  if (u === "each") {
    // each should be integer-ish but not guaranteed
    // if fractional, keep 3 decimals
    const isIntLike = Math.abs(n - Math.round(n)) < 1e-9;
    return isIntLike ? `${Math.round(n)} each` : `${n.toFixed(3)} each`;
  }

  // Unknown unit => show number only
  return `${n.toFixed(3)}`;
}

/* ----------------------------- component ----------------------------- */

export default function Replenishment() {
  const qs = new URLSearchParams(useLocation().search);
  const token = qs.get("token");
  const hasToken = !!token;

  const [avail, setAvail] = useState(null);
  const [loadingToken, setLoadingToken] = useState(!!token);
  const [error, setError] = useState("");

  // Manual / forecasting mode state
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [lines, setLines] = useState([{ menu_item_id: "", servings: "", price_per_serving: "" }]);
  const [calcLoading, setCalcLoading] = useState(false);

  // request cancellation (prevents UI race / flashing)
  const inFlightRef = useRef(null);

  // ---------- Token mode: fetch availability for event plan ----------
  useEffect(() => {
    let cancelled = false;
    if (!token) return;

    // cancel prior request
    try { inFlightRef.current?.abort?.(); } catch {}
    const controller = new AbortController();
    inFlightRef.current = controller;

    (async () => {
      try {
        setLoadingToken(true);
        setError("");
        const { data } = await api.get("/inventory/replenishment/availability", {
          params: { token },
          signal: controller.signal,
        });
        if (!cancelled) setAvail(data);
      } catch (e) {
        if (!cancelled) {
          if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
            setError(e?.response?.data?.error || "Failed to load availability");
          }
        }
      } finally {
        if (!cancelled) setLoadingToken(false);
      }
    })();

    return () => {
      cancelled = true;
      try { controller.abort(); } catch {}
    };
  }, [token]);

  // ---------- Manual mode: preload locations & menu items ----------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data: locData } = await api.get("/inventory/locations", {
          params: { active_only: 1 },
        });
        if (!cancelled) {
          const list = Array.isArray(locData) ? locData : [];
          setLocations(list);

          // Better default:
          // 1) is_default_goods_received
          // 2) name contains "main"
          // 3) first
          if (!locationId && list.length > 0) {
            const preferred =
              list.find((l) => l.is_default_goods_received) ||
              list.find((l) => /main/i.test(String(l.name))) ||
              list[0];
            if (preferred?.location_id != null) setLocationId(String(preferred.location_id));
          }
        }
      } catch (e) {
        console.error("Failed to load locations", e);
        if (!cancelled) setLocations([]);
      }

      try {
        const { data: miData } = await api.get("/inventory/menu-items");
        if (!cancelled) {
          setMenuItems(Array.isArray(miData) ? miData : []);
        }
      } catch (e) {
        console.error("Failed to load menu items", e);
        if (!cancelled) setMenuItems([]);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Manual mode helpers ----------
  const updateLine = (idx, patch) => {
    setLines((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...patch };
      return copy;
    });
  };

  const addLine = () => {
    setLines((prev) => [...prev, { menu_item_id: "", servings: "", price_per_serving: "" }]);
  };

  const removeLine = (idx) => {
    setLines((prev) => {
      if (prev.length === 1) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };

  const handleMenuChange = (idx, menuItemId) => {
    const midStr = String(menuItemId || "");
    const mid = midStr ? Number(midStr) : null;
    const menu = menuItems.find((m) => Number(m.id) === mid || Number(m.menu_item_id) === mid);

    setLines((prev) => {
      const copy = [...prev];
      const line = { ...copy[idx], menu_item_id: midStr };

      // Autofill price per serving if empty and menu has override
      if ((!line.price_per_serving || line.price_per_serving === "") && menu && menu.manual_sale_price_override != null) {
        line.price_per_serving = String(Number(menu.manual_sale_price_override));
      }

      copy[idx] = line;
      return copy;
    });
  };

  const handleCalculate = async () => {
    setError("");

    const locNum = Number(locationId);
    if (!Number.isFinite(locNum) || locNum <= 0) {
      setError("Please select a valid location.");
      return;
    }

    const payloadItems = [];
    for (const line of lines) {
      const mid = Number(line.menu_item_id || 0);
      const servings = Number(line.servings || 0);
      if (!Number.isFinite(mid) || mid <= 0 || servings <= 0) continue;

      let price = null;
      if (line.price_per_serving !== "") {
        const p = Number(line.price_per_serving);
        price = Number.isFinite(p) && p > 0 ? p : null;
      }

      // Fallback to menu item's sale price override if not provided
      if (price == null) {
        const menu = menuItems.find((m) => Number(m.id) === mid || Number(m.menu_item_id) === mid);
        if (menu && menu.manual_sale_price_override != null) {
          const p = Number(menu.manual_sale_price_override);
          price = Number.isFinite(p) && p > 0 ? p : null;
        }
      }

      payloadItems.push({
        menu_item_id: mid,
        servings_requested: servings,
        resolved_sale_price: price,
      });
    }

    if (!payloadItems.length) {
      setError("Add at least one menu item with a valid serving quantity.");
      return;
    }

    // cancel prior request
    try { inFlightRef.current?.abort?.(); } catch {}
    const controller = new AbortController();
    inFlightRef.current = controller;

    try {
      setCalcLoading(true);
      const { data } = await api.post(
        "/inventory/replenishment/availability",
        { location_id: locNum, items: payloadItems },
        { signal: controller.signal }
      );
      setAvail(data);
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
        console.error("Manual availability error:", e);
        setError(e?.response?.data?.error || "Failed to compute availability");
      }
    } finally {
      setCalcLoading(false);
    }
  };

  // ---------- derived UI data ----------
  const currentLocationName = useMemo(() => {
    return locations.find((l) => String(l.location_id) === String(locationId))?.name || null;
  }, [locations, locationId]);

  const ingredientRows = Array.isArray(avail?.ingredients) ? avail.ingredients : [];

  // If backend later adds base_uom per ingredient, we’ll use it.
  // Otherwise we can’t know unit reliably — but with your new backend, base is usually g/ml/each.
  const guessBaseUom = (r) => r?.base_uom || r?.uom || null;

  // ---------- UI ----------
  return (
    <div className="min-h-screen p-6 md:p-10 bg-gradient-to-br from-black via-gray-900 to-black text-white">
      <h1 className="text-2xl font-bold mb-4">Replenishment / Forecasting</h1>

      {/* Token mode banner */}
      {hasToken && (
        <div className="mb-4 text-sm text-gray-300">
          Token mode: computing availability for a planned event (token expires ~20 minutes after creation).
          {loadingToken && (
            <span className="ml-2">
              <Loader className="w-4 h-4 inline-block animate-spin mr-2" /> Loading…
            </span>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 bg-rose-600/20 border border-rose-600 text-rose-100 px-3 py-2 rounded inline-flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Manual / Forecasting Panel */}
      <div className="mb-6 bg-white/5 rounded-xl border border-white/10 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div>
            <h2 className="font-semibold text-lg">Manual Forecast / Replenishment</h2>
            <p className="text-xs text-gray-300">
              Build a plan directly from menu items for any future event or period, without creating an Event first.
            </p>
          </div>
        </div>

        {/* Location */}
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div className="md:col-span-1">
            <label className="text-xs text-gray-300 block mb-1">Location</label>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select location…</option>
              {locations.map((loc) => (
                <option key={loc.location_id ?? loc.name} value={loc.location_id}>
                  {loc.name || `Location #${loc.location_id}`}
                </option>
              ))}
            </select>

            {currentLocationName && (
              <div className="mt-1 text-[11px] text-gray-400">
                Selected: <span className="text-gray-200">{currentLocationName}</span>
              </div>
            )}
          </div>
        </div>

        {/* Lines */}
        <div className="space-y-3">
          {lines.map((line, idx) => {
            const mid = Number(line.menu_item_id || 0);
            const menu = menuItems.find((m) => Number(m.id) === mid || Number(m.menu_item_id) === mid);
            const defaultPrice =
              menu && menu.manual_sale_price_override != null ? Number(menu.manual_sale_price_override) : null;

            return (
              <div
                key={idx}
                className="grid md:grid-cols-12 gap-3 bg-black/40 border border-white/10 rounded-xl p-3"
              >
                <div className="md:col-span-6">
                  <label className="text-xs text-gray-300 block mb-1">Menu item</label>
                  <select
                    value={line.menu_item_id}
                    onChange={(e) => handleMenuChange(idx, e.target.value)}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Select dish/drink…</option>
                    {menuItems.map((mi) => (
                      <option key={mi.id || mi.menu_item_id} value={mi.id || mi.menu_item_id}>
                        {mi.name || mi.menu_item_name} {mi.type ? `(${mi.type})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs text-gray-300 block mb-1">Servings</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={line.servings}
                    onChange={(e) => updateLine(idx, { servings: e.target.value })}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. 50"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs text-gray-300 block mb-1">Price per serving (£)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.price_per_serving}
                    onChange={(e) => updateLine(idx, { price_per_serving: e.target.value })}
                    className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2 text-sm"
                    placeholder={defaultPrice != null ? `Default: £${defaultPrice.toFixed(2)}` : "Optional"}
                  />
                </div>

                <div className="md:col-span-12 flex justify-end">
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-lg border border-rose-500/50 text-rose-200 hover:bg-rose-500/10"
                  >
                    <Trash2 className="w-3 h-3" />
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-3 justify-between">
          <button
            type="button"
            onClick={addLine}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
          >
            <PlusCircle className="w-4 h-4" />
            Add Dish / Drink
          </button>

          <button
            type="button"
            onClick={handleCalculate}
            disabled={calcLoading}
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60"
          >
            {calcLoading && <Loader className="w-4 h-4 animate-spin" />}
            Run Forecast
          </button>
        </div>
      </div>

      {/* Results */}
      {avail && (
        <>
          <div className="grid md:grid-cols-3 gap-4">
            {/* Ingredients rollup */}
            <div className="md:col-span-2 bg-white/5 rounded-xl border border-white/10 p-4">
              <h3 className="font-semibold mb-2">Ingredients (rollup)</h3>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Required</th>
                      <th className="px-3 py-2 text-right">On Hand</th>
                      <th className="px-3 py-2 text-right">Deficit</th>
                      <th className="px-3 py-2 text-right">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingredientRows.map((r) => {
                      const u = guessBaseUom(r);
                      const deficit = toNum(r.deficit_qty, 0);

                      return (
                        <tr key={r.item_id} className="odd:bg-white/0 even:bg-white/5">
                          <td className="px-3 py-2">
                            {r.item_code ? `${r.item_code} — ` : ""}
                            {r.item_name}
                          </td>
                          <td className="px-3 py-2 text-right">{formatQtyWithUnit(r.required_qty, u)}</td>
                          <td className="px-3 py-2 text-right">{formatQtyWithUnit(r.on_hand, u)}</td>
                          <td className={`px-3 py-2 text-right ${deficit > 0 ? "text-rose-300 font-semibold" : ""}`}>
                            {formatQtyWithUnit(r.deficit_qty, u)}
                          </td>
                          <td className="px-3 py-2 text-right">{moneyGBP(r.est_cost)}</td>
                        </tr>
                      );
                    })}

                    {ingredientRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-300">
                          No ingredients found for this selection.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 text-[11px] text-gray-400">
                Note: quantities are shown in base units (g / ml / each) when available.
              </div>
            </div>

            {/* Totals / feasible */}
            <div className="bg-white/5 rounded-xl border border-white/10 p-4">
              <h3 className="font-semibold mb-2">Totals</h3>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Required Cost</span>
                  <span>{moneyGBP(avail.totals?.required_cost_sum || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Projected Revenue</span>
                  <span>{moneyGBP(avail.totals?.projected_revenue || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Projected GP</span>
                  <span>{moneyGBP(avail.totals?.projected_gp || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>COGS %</span>
                  <span>
                    {avail.totals?.cogs_pct == null ? "—" : `${Number(avail.totals.cogs_pct).toFixed(1)}%`}
                  </span>
                </div>
              </div>

              <h3 className="font-semibold mt-5 mb-2">Feasible servings (by dish)</h3>
              <div className="space-y-1 text-sm">
                {(avail.feasible?.per_menu_item || []).map((p) => (
                  <div key={p.menu_item_id} className="flex justify-between">
                    <span>Item #{p.menu_item_id}</span>
                    <span>
                      {p.feasible_now} / {p.requested}
                    </span>
                  </div>
                ))}
                <div className="mt-2 border-t border-white/10 pt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span>
                    {avail.feasible?.overall?.feasible_total_servings ?? 0} /{" "}
                    {avail.feasible?.overall?.requested_total_servings ?? 0}
                  </span>
                </div>
              </div>

              {(avail.warnings || []).length > 0 && (
                <div className="mt-4 bg-amber-100 text-amber-900 border border-amber-300 px-3 py-2 rounded text-xs">
                  Some items have no balance row at this location. They are treated as on-hand = 0.
                </div>
              )}
            </div>
          </div>

          {/* Vendor groups (optional; only render if backend provides it) */}
          {Array.isArray(avail.vendor_groups) && avail.vendor_groups.length > 0 && (
            <div className="mt-6 bg-white/5 rounded-xl border border-white/10 p-4">
              <h3 className="font-semibold mb-2">Suggested PO Groups (by Vendor)</h3>
              <p className="text-xs text-gray-300 mb-3">
                Suggested lines based on deficit quantities.
              </p>
              <div className="space-y-4">
                {avail.vendor_groups.map((g) => (
                  <div key={g.vendor_id || "unassigned"} className="border border-white/10 rounded-lg p-3">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-semibold">{g.vendor_name || "Unassigned Vendor"}</span>
                      <span className="text-emerald-300 font-semibold">
                        Est. Total: {moneyGBP(g.total_cost || 0)}
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-white/70">
                            <th className="text-left px-2 py-1">Item</th>
                            <th className="text-right px-2 py-1">Deficit</th>
                            <th className="text-right px-2 py-1">Unit Cost</th>
                            <th className="text-right px-2 py-1">Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(g.items || []).map((it) => (
                            <tr key={it.item_id} className="border-t border-white/10">
                              <td className="px-2 py-1">
                                {it.item_code ? `${it.item_code} — ` : ""}
                                {it.item_name}
                              </td>
                              <td className="px-2 py-1 text-right">{toNum(it.deficit_qty, 0).toFixed(3)}</td>
                              <td className="px-2 py-1 text-right">£{toNum(it.unit_cost, 0).toFixed(4)}</td>
                              <td className="px-2 py-1 text-right">{moneyGBP(it.line_cost || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {!hasToken && !avail && (
        <div className="mt-4 text-xs text-gray-300">
          Use the panel above to build a replenishment / forecast plan from menu items.
        </div>
      )}
    </div>
  );
}